import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "isomorphic-style-loader/lib/withStyles";
import cx from "classnames";
import s from "./ScanQRCodeModal.css";
import cs from "../../components/commonStyle.css";
import { Modal, Button } from "react-bootstrap";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import messages from "../../locale/messages";
import { MdContentCopy } from "react-icons/md";
import { FaCheck } from "react-icons/fa6";
import { toastr } from "react-redux-toastr";
import clockIcon from "../../../public/SiteIcons/timerIcon.svg";
import QRCode from "react-qr-code";

class ScanQRCodeModal extends Component {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool.isRequired,
    qrCode: PropTypes.string,
    verificationLink: PropTypes.string,
    isExpired: PropTypes.bool,
    onRegenerateQR: PropTypes.func,
    expiresAt: PropTypes.string,
    isLoading: PropTypes.bool,
    error: PropTypes.string,
    errorCode: PropTypes.string,
    listId: PropTypes.string,
    category: PropTypes.string,
    qrId: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      copied: false,
      timeLeft: 900, // 15 minutes in seconds
      isRefreshing: false,
      savedTimeLeft: null, // Add this to save timer state
    };
    this.copyToClipboard = this.copyToClipboard.bind(this);
    this.updateTimer = this.updateTimer.bind(this);
    this.refreshQRCode = this.refreshQRCode.bind(this);
  }

  componentDidMount() {
    if (this.props.show) {
      // Use saved time if available, otherwise start fresh
      const startTime = this.state.savedTimeLeft || 900; // Use 15 minutes if no saved time
      this.setState({ timeLeft: startTime }, () => {
        this.startTimer();
      });
    }
  }

  componentDidUpdate(prevProps) {
    // Start timer when modal is shown
    if (!prevProps.show && this.props.show) {
      // Use saved time if available, otherwise start fresh
      const startTime = this.state.savedTimeLeft || 900; // Use 15 minutes if no saved time
      this.setState({ timeLeft: startTime }, () => {
        this.startTimer();
      });
    }
    // Save timer state when modal is hidden
    if (prevProps.show && !this.props.show) {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        // Save current time left
        this.setState({ savedTimeLeft: this.state.timeLeft });
      }
    }
    // Reset timer when QR code is regenerated
    if (this.props.qrCode !== prevProps.qrCode) {
      this.resetAndStartTimer();
    }
  }

  resetAndStartTimer() {
    // Clear any existing timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    // Reset time to 15 minutes
    this.setState(
      {
        timeLeft: 900, // 15 minutes in seconds
        isExpired: false,
        isRefreshing: false,
        savedTimeLeft: 900, // Reset saved time as well (15 minutes)
      },
      () => {
        // Start the timer after state is updated
        this.startTimer();
      }
    );
  }

  startTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(this.updateTimer, 1000);
  }

  updateTimer() {
    this.setState((prevState) => {
      const newTimeLeft = prevState.timeLeft - 1;
      if (newTimeLeft <= 0) {
        clearInterval(this.timerInterval);
        return {
          timeLeft: 0,
          isExpired: true,
        };
      }
      return { timeLeft: newTimeLeft };
    });
  }

  formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  async copyToClipboard() {
    const { verificationLink } = this.props;
    try {
      await navigator.clipboard.writeText(verificationLink);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 3000);
      toastr.success("Success", "Link copied to clipboard");
    } catch (err) {
      toastr.error("Error", "Failed to copy link");
    }
  }

  async refreshQRCode() {
    this.setState({ isRefreshing: true });

    try {
      await this.props.onRegenerateQR();
      // Explicitly reset and start timer after successful regeneration
      this.resetAndStartTimer();
    } catch (error) {
      console.error("Error regenerating QR code:", error);
      this.setState({ isRefreshing: false });
      toastr.error("Error", "Failed to regenerate QR code");
    }
  }

  handleClose = () => {
    // Clear timer when modal is closed
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    // Save current time left before closing
    this.setState({
      isRefreshing: false,
      copied: false,
      savedTimeLeft: this.state.timeLeft,
    });
    // Call parent's onClose
    this.props.onClose();
  };

  componentWillUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  getQRCodeScript() {
    const { userId, listId, category, qrId } = this.props;
    // Create URL to the public redirect page with parameters
    return `https://www.motostream.app/redirect.html?userId=${userId}&listId=${listId}&category=${category}&qrId=${qrId}`;
  }
  render() {
    const { show, onClose, isExpired, isLoading, error, errorCode } =
      this.props;
    const { copied, timeLeft, isRefreshing } = this.state;
    const { formatMessage } = this.props.intl;

    if (!show) return null;

    return (
      <Modal
        show={show}
        animation={false}
        onHide={onClose}
        className="qrCodeModal"
      >
        <div className={cx(s.sectionBlock)}>
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body>
            {error ? (
              <div className={cx(s.errorContent, cs.textAlignCenter)}>
                <h2 className={cx(s.titleText)}>Unable to Generate QR Code</h2>
                <p className={cx(s.errorMessage)}>{error}</p>
                {errorCode === "402" && (
                  <p className={cx(s.errorHint)}>
                    Please contact support for assistance with your quota limit.
                  </p>
                )}
                <Button
                  className={cx(s.regenerateBtn)}
                  onClick={this.refreshQRCode}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? "Trying..." : "Try Again"}
                </Button>
              </div>
            ) : !isExpired ? (
              <>
                <div className={cx(s.scanContent, cs.textAlignCenter)}>
                  <h2 className={cx(s.titleText)}>Scan QR Code to Continue</h2>
                  <p className={cx(s.contentText)}>
                    Use your phone's camera or a QR scanner app to open the
                    verification page.
                  </p>
                  {!isLoading && (
                    <div className={s.qrCodeContainer}>
                      <QRCode
                        value={this.getQRCodeScript()}
                        size={256}
                        viewBox={`0 0 256 256`}
                        level="H"
                        className={cx(s.qrCode)}
                      />
                      {!isExpired && (
                        <div className={s.qrExpiry}>
                          <img src={clockIcon} alt="clock" />
                          QR expires in:{" "}
                          <span className={s.highlight}>
                            {this.formatTime(timeLeft)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className={cx(s.copyTextContainer)}>
                  <h4 className={cx(s.copyTextTitle)}>Other Ways to Verify:</h4>
                  <div className={cx(s.copyTextBtnContainer)}>
                    {!copied ? (
                      <Button
                        className={cx(s.copyBtn, cs.btn)}
                        onClick={this.copyToClipboard}
                      >
                        <span className={cx(s.copyTextBtn)}>
                          Copy Verification Link
                        </span>
                        <MdContentCopy />
                      </Button>
                    ) : (
                      <Button className={cx(s.copyBtn, cs.btn)}>
                        <span className={cx(s.copiedTextBtn)}>Copied</span>
                        <FaCheck />
                      </Button>
                    )}
                  </div>
                  <p className={cx(s.copyTextContent)}>
                    Open this link on your mobile to complete verification.
                  </p>
                </div>
              </>
            ) : (
              <div className={cx(s.expiredContent, cs.textAlignCenter)}>
                <h2 className={cx(s.titleText)}>This QR code has expired</h2>
                <p className={cx(s.contentText)}>
                  Generate a new one to continue verification.
                </p>
                {!isLoading && (
                  <div className={cx(s.qrCodeContainer, s.expired)}>
                    <QRCode
                      value={this.getQRCodeScript()}
                      size={256}
                      viewBox={`0 0 256 256`}
                      level="H"
                      className={cx(s.qrCode, s.expired)}
                    />
                  </div>
                )}
                <Button
                  className={cx(s.regenerateBtn)}
                  onClick={this.refreshQRCode}
                  disabled={isRefreshing}
                >
                                 {isRefreshing ? (
                   <FormattedMessage {...messages.verificationGenerating} />
                 ) : (
                   <FormattedMessage {...messages.verificationRegenerateQr} />
                 )}
                </Button>
              </div>
            )}
          </Modal.Body>
        </div>
      </Modal>
    );
  }
}

const mapState = (state) => ({});

const mapDispatch = {};

export default injectIntl(
  withStyles(s, cs)(connect(mapState, mapDispatch)(ScanQRCodeModal))
);
